import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import DraggableList from 'react-draggable-list';
import Animated from 'react-native-reanimated';
import { View, Pressable, StyleSheet, } from 'react-native';
import { AppView, rem, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS, Spacer, withAnimatedNavigationHeader, } from 'design-system';
import { useMarginBottom } from 'hooks/useMarginBottom';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import { IconBoldDrag } from 'design-system/icons/IconBoldDrag';
import Text, { styles as textStyles } from 'design-system/Text';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setPotSpace } from 'features/pots/actions';
import { setTradingAccountSpace } from 'actions/invest';
import iconSource from 'design-system/img/notifications.png';
import PaymentToastModal from 'features/pay/components/toast/PaymentToastModal';
import { forceRefreshAnalytics, getBankConnections, getCategories, getFeed, } from 'actions';
import { muteAll, useFetchOld, useTimeout } from 'hooks';
import { benefits } from 'features/premium/benefits';
import { Tier } from 'features/premium/entities';
import { UpgradeWebModal } from 'design-system/UpgradeWebModal';
import { selectUserId } from 'reducers/selectors';
import useColors from 'hooks/useColors';
import Modal from 'design-system/ModalWeb';
import { IconBell } from 'design-system/icons';
import { selectSpaceInfo } from './reducers/selectors';
import { useMySpaceAccounts } from './hooks/useMySpaceAccounts';
import { AccountItem } from './components';
import { editSpaceInfoSuccess, fetchSpacesAction, sendSpaceReminder, setAccountSpace, } from './actions';
const ITEM_HEIGHT = rem(78);
const keyExtractor = (item) => item.id.toString();
const Header = memo(({ id, spaceName, spaceAccountsCount, style, }) => {
    const styles = useStyles(headerStyleSet);
    const opacity = useMemo(() => (spaceAccountsCount[id] ? 0 : 1), [id, spaceAccountsCount]);
    return (<View style={[styles.container, style]}>
        <Text Text-16 Secondary>
          {spaceName} ({spaceAccountsCount[id]})
        </Text>
        <Spacer h={14}/>
        <Text TextThin-16 Secondary style={[styles.empty, { opacity }]}>
          Drag accounts to add to this space
        </Text>
      </View>);
});
const headerStyleSet = createStyleSheets((colors) => ({
    container: {
        borderTopLeftRadius: rem(16),
        borderTopRightRadius: rem(16),
        paddingTop: rem(32),
        marginHorizontal: rem(16),
        position: 'relative',
    },
    empty: {
        ...textStyles.TextThin_16,
        color: colors.text.secondary,
        textAlign: 'center',
        position: 'absolute',
        bottom: -rem(16),
        width: '100%',
    },
}));
const DraggableItem = memo(({ account, itemSelected, dragHandleProps, }) => {
    const styles = useStyles(draggableStyleSet);
    const scale = itemSelected * 0.05 + 1;
    return (<Pressable {...dragHandleProps} style={styles.draggable}>
        <View style={[styles.account, { transform: [{ scale }] }]}>
          <View style={styles.accountRow}>
            <View style={styles.flex}>
              <AccountItem account={account} disabled hideSpaceUserAvatar/>
            </View>
            <IconBoldDrag />
          </View>
        </View>
      </Pressable>);
});
const draggableStyleSet = createStyleSheets((colors) => ({
    flex: {
        flex: 1,
    },
    draggable: {
        paddingTop: rem(10),
        marginHorizontal: rem(16),
    },
    account: {
        justifyContent: 'center',
        height: ITEM_HEIGHT,
        borderRadius: rem(16),
        backgroundColor: colors.cards.onDark,
    },
    accountRow: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        paddingRight: rem(16),
        paddingLeft: rem(8),
    },
}));
const animationConfig = {
    dampingRatio: 3,
    duration: 300,
};
const AllSpaceAccountsDraggable = withAnimatedNavigationHeader(({ navigation, scrollHandler }) => {
    const { mySpaceAccounts } = useMySpaceAccounts();
    const [movedAccounts, setMovedAccounts] = useState({});
    const hasMovedAccounts = useMemo(() => Object.entries(movedAccounts).length > 0, [movedAccounts]);
    const dispatch = useAppDispatch();
    const [isMovingAccounts, setIsMovingAccounts] = useState(false);
    const onMoveAccounts = useCallback(async () => {
        setIsMovingAccounts(true);
        const promises = [];
        const editedSpaceIds = new Set();
        promises.push(...Object.entries(movedAccounts).map(([, { account, newSpaceId }]) => {
            editedSpaceIds.add(newSpaceId);
            if ('productId' in account) {
                return dispatch(setPotSpace(String(account.id), newSpaceId));
            }
            if ('isEmmaInvestmentAccount' in account) {
                return dispatch(setTradingAccountSpace(newSpaceId, account.id));
            }
            return dispatch(setAccountSpace(account.id, newSpaceId));
        }));
        try {
            await Promise.all(promises);
            dispatch(fetchSpacesAction());
            dispatch(getBankConnections());
            dispatch(forceRefreshAnalytics());
            dispatch(getCategories());
            dispatch(getFeed());
            editedSpaceIds.forEach((spaceId) => dispatch(editSpaceInfoSuccess(spaceId)));
            setIsMovingAccounts(false);
            navigation.pop();
        }
        catch {
            setIsMovingAccounts(false);
        }
    }, [movedAccounts]);
    const [listA, setListA] = useState(() => mySpaceAccounts
        .map(({ space, accounts }) => [
        {
            id: space.id,
            spaceName: space.isDefault ? 'Private Space' : space.name,
        },
        ...accounts,
    ])
        .flat());
    const [spaceAccountsCount, setSpaceAccountsCount] = useState(mySpaceAccounts.reduce((acc, curr) => ({
        ...acc,
        [curr.space.id]: curr.accounts.length,
    }), {}));
    const fromSpaceIdRef = useRef(null);
    const updateMovedAccounts = useCallback((toSpaceId, movedAccount) => {
        if (!movedAccount || 'spaceName' in movedAccount)
            return;
        setMovedAccounts((prev) => {
            if (toSpaceId === movedAccount.spaceId) {
                const newState = { ...prev };
                delete newState[movedAccount.id];
                return newState;
            }
            return {
                ...prev,
                [movedAccount.id]: {
                    account: movedAccount,
                    newSpaceId: toSpaceId,
                },
            };
        });
    }, []);
    const onDragBegin = useCallback((item) => {
        const index = listA.indexOf(item);
        const fromSpace = listA
            .slice(0, index)
            .findLast((item) => 'spaceName' in item);
        if (fromSpace && typeof fromSpace.id === 'number') {
            fromSpaceIdRef.current = fromSpace.id;
        }
    }, [listA]);
    const renderItem = useCallback(({ item, itemSelected, dragHandleProps, }) => {
        if ('spaceName' in item) {
            return (<Header {...item} spaceAccountsCount={spaceAccountsCount} style={item.id === listA[0].id ? styles.firstHeader : undefined}/>);
        }
        return (<DraggableItem account={item} itemSelected={itemSelected} dragHandleProps={dragHandleProps}/>);
    }, [listA, spaceAccountsCount]);
    const [, , , fetchSendReminder] = useFetchOld(muteAll);
    const [reminderName, setReminderName] = useState(null);
    useTimeout(() => setReminderName(null), reminderName ? 3000 : undefined);
    const colors = useColors();
    const spaceInfoRecord = useAppSelector(selectSpaceInfo);
    const userId = useAppSelector(selectUserId);
    const onSendReminder = useCallback(async (id, createdBy) => {
        const res = await fetchSendReminder(sendSpaceReminder(id, 'SPACE_UPGRADE_REMINDER'));
        if (!res)
            return;
        setReminderName(spaceInfoRecord[id]?.users?.find(({ id }) => createdBy === id)
            ?.firstName || 'owner');
    }, [fetchSendReminder, spaceInfoRecord]);
    const onUpgrade = useCallback(() => {
        if (benefits.spaces.minTier !== Tier.free) {
            UpgradeWebModal.show(benefits.spaces.minTier);
        }
    }, []);
    const onMoveEnd = useCallback((newList, movedItem, fromIndex, toIndex) => {
        const fromSpaceId = fromSpaceIdRef.current;
        if (fromSpaceId === null || fromIndex === null || toIndex === null) {
            return;
        }
        const toSpaceId = [...newList]
            .slice(0, toIndex)
            .findLast((item) => 'spaceName' in item)?.id;
        const space = toSpaceId ? spaceInfoRecord[toSpaceId] : null;
        if (!toSpaceId || !space)
            return;
        if (fromSpaceId !== toSpaceId && typeof toSpaceId === 'number') {
            const { isDefault, isPremium, id, createdBy } = space.space;
            if (!isDefault && !isPremium) {
                setTimeout(() => {
                    if (createdBy === userId) {
                        Modal.showConfirmation({
                            title: 'You can’t move accounts to this space',
                            subtitle: 'If you want to move accounts and continue using this space, you need to upgrade your current plan.\n\nDo you want to upgrade?',
                            confirmButtonTitle: 'No, thanks',
                            cancelButtonTitle: 'Yes, upgrade plan',
                            onPressCancel: onUpgrade,
                        });
                        return;
                    }
                    Modal.showConfirmation({
                        title: 'You can’t add accounts to this space',
                        subtitle: 'If you want to add accounts and continue using this space, contact the owner of the space to upgrade their plan',
                        confirmButtonTitle: 'Remind them to upgrade',
                        cancelButtonTitle: 'Okay',
                        onConfirm: () => onSendReminder(id, createdBy),
                        confirmButtonLeftIcon: (<IconBell color={colors.text.textOnBrand}/>),
                    });
                }, 600);
                fromSpaceIdRef.current = null;
                return;
            }
            updateMovedAccounts(toSpaceId, movedItem);
            setSpaceAccountsCount((prev) => ({
                ...prev,
                [fromSpaceId]: prev[fromSpaceId] - 1,
                [toSpaceId]: prev[toSpaceId] + 1,
            }));
        }
        setListA([...newList]);
        fromSpaceIdRef.current = null;
    }, [
        colors.text.textOnBrand,
        onSendReminder,
        onUpgrade,
        spaceInfoRecord,
        updateMovedAccounts,
        userId,
    ]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(true);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING + SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    const containerRef = useRef(null);
    const container = useCallback(() => containerRef.current, []);
    return (<AppView withFrame={false}>
          <PaymentToastModal isVisible={!!reminderName} onCloseAction={() => setReminderName(null)} title="Sent a reminder to upgrade" message={`To ${reminderName}`} iconSource={iconSource} style={styles.toast}/>
          <Animated.ScrollView ref={containerRef} onScroll={scrollHandler} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} scrollEventThrottle={16} contentContainerStyle={contentContainerStyle}>
            <DraggableList list={listA} itemKey={keyExtractor} 
    // @ts-ignore
    template={renderItem} padding={0} onDragStart={onDragBegin} springConfig={animationConfig} onMoveEnd={onMoveEnd} autoScrollRegionSize={200} container={container}/>
          </Animated.ScrollView>
          <Button title="Save" floating onPress={onMoveAccounts} brand disabled={!hasMovedAccounts} containerStyle={!hasMovedAccounts ? styles.hidden : null} isFetching={isMovingAccounts}/>
        </AppView>);
}, ({ colors }) => ({
    title: 'All your accounts',
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    hidden: {
        opacity: 0,
    },
    firstHeader: {
        paddingTop: rem(16),
    },
    toast: {
        marginTop: rem(16),
        paddingTop: 0,
    },
});
export default AllSpaceAccountsDraggable;
